import {createRouter, createWebHistory, RouteRecordRaw} from 'vue-router';
import LoginDialog from '@/views/authentication/LoginDialog.vue';
import ReportLanding from '@/views/header/ReportLanding.vue';
import AdminLanding from '@/views/header/AdminLanding.vue';
import AcceptLanding from '@/views/header/AcceptLanding.vue';
import DashboardLanding from '@/views/header/DashboardLanding.vue';
import TabKpisMijlpalen from '@/views/admin/TabKpisMijlpalen.vue';
import TabGebruikers from '@/views/shared/TabGebruikers.vue';
import TabInstellingen from '@/views/admin/TabInstellingen.vue';
import TabThemas from '@/views/admin/TabThemas.vue';
import PasswordResetDialog from '@/views/authentication/PasswordResetDialog.vue';
import TabBeheerAdmin from '@/views/admin/TabBeheerAdmin.vue';
import TabReportItems from '@/views/admin/TabReportItems.vue';
import TabVecozo from '@/views/admin/TabVecozo.vue';
import TabAltView from '@/views/admin/TabAltView.vue';
import {meHasPermission} from '@/base/me.js';
import TabDashboard from '@/views/admin/TabDashboard.vue';
import TabOverall from '@/views/dashboard/TabOverall.vue';
import TabPersoneel from '@/views/dashboard/TabPersoneel.vue';
import TabInitiatieven from '@/views/dashboard/TabInitiatieven.vue';
import TabFinancien from '@/views/dashboard/TabFinancien.vue';
import TabKpis from '@/views/dashboard/TabKpis.vue';
import TabToegankelijkheid from '@/views/dashboard/TabToegankelijkheid.vue';
import TabQuadrupleAim from '@/views/dashboard/TabQuadrupleAim.vue';
import TabDashboardData from '@/views/admin/TabDashboardData.vue';
import TabRapporteren from '@/views/reporter/TabRapporteren.vue';
import TabHeden from '@/views/reporter/TabHeden.vue';
import TabBeheerReporter from '@/views/reporter/TabBeheerReporter.vue';
import TabTeDoen from '@/views/acceptant/TabTeDoen.vue';
import TabGeaccepteerd from '@/views/acceptant/TabGeaccepteerd.vue';
import TabAfgewezen from '@/views/acceptant/TabAfgewezen.vue';
import { Permission, Role } from 'common';
import ReporterManagerLanding from '@/views/header/ReporterManagerLanding.vue';
 
export enum KpiSelectEnum {
    onlyMilestones,
    allOthers
}

// the dashboard sub-routes under a landing: /admin/dashboard, /report/dashboard and the viewer's /dashboard share these;
// path is the (nested) route path, base the absolute prefix that the tab links and the redirect use
function dashboardRoute(path: string, base: string): RouteRecordRaw {
    return {
        path     : path,
        component: TabDashboard,
        meta     : {requires: Permission.dashboard, base: base},
        redirect : () => `${base}/${lastSegment(getStoredTab('dashboard', `${base}/overall`))}`,
        children : [
            {path: 'overall',          component: TabOverall,          meta: {requires: Permission.dashboard}},
            {path: 'personeel',        component: TabPersoneel,        meta: {requires: Permission.dashboard}},
            {path: 'initiatieven',     component: TabInitiatieven,     meta: {requires: Permission.dashboard}},
            {path: 'financien',        component: TabFinancien,        meta: {requires: Permission.dashboard}},
            {path: 'kpis',             component: TabKpis,             meta: {requires: Permission.dashboard}},
            {path: 'toegankelijkheid', component: TabToegankelijkheid, meta: {requires: Permission.dashboard}},
            {path: 'quadruple-aim',    component: TabQuadrupleAim,     meta: {requires: Permission.dashboard}},
            // requires admin, so the guard stores it under activeTab:admin, not activeTab:dashboard
            {path: 'data',             component: TabDashboardData,    meta: {requires: Permission.admin}},
        ],
    };
}

// the stored dashboard tab may come from another landing (/admin/dashboard/kpis while on /report/dashboard); only its tab name is reused
function lastSegment(path: string): string {
    return path.substring(path.lastIndexOf('/') + 1);
}

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    routes : [
        {
            path     : '/',
            name     : 'login',
            component: LoginDialog,
        },
        {
            path     : '/auth/reset-password',
            name     : 'password reset',
            component: PasswordResetDialog,
        },
        {
            path     : '/admin',
            name     : 'admin',
            component: AdminLanding,
            meta     : {requires: 'admin'},
            redirect : () => getStoredTab('admin', '/admin/thema'),
            children : [
                {
                    path     : 'thema',
                    component: TabThemas,
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'organization',
                    component: TabInstellingen,
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'user',
                    component: TabGebruikers,
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'milestone',
                    component: TabKpisMijlpalen,
                    props    : {kpiSelect: KpiSelectEnum.onlyMilestones},
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'kpi',
                    component: TabKpisMijlpalen,
                    props    : {kpiSelect: KpiSelectEnum.allOthers},
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'status',
                    component: TabReportItems,
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'altview',
                    component: TabAltView,
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'vecozo',
                    component: TabVecozo,
                    meta     : {requires: 'admin'},
                },
                {
                    path     : 'admin',
                    component: TabBeheerAdmin,
                    meta     : {requires: 'admin'},
                },
                dashboardRoute('dashboard', '/admin/dashboard'),
            ],
        },
        {
            path     : '/report',
            name     : 'report',
            component: ReportLanding,
            meta     : {requires: 'report'},
            redirect : () => getStoredTab('report', '/report/rapporteren'),
            children : [
                {
                    path     : 'rapporteren',
                    component: TabHeden,
                    meta     : {requires: 'report'},
                },
                {
                    path     : 'al-mijn-kpis',
                    component: TabRapporteren,
                    meta     : {requires: 'report'},
                },
                {
                    path     : 'beheer',
                    component: TabBeheerReporter,
                    meta     : {requires: 'report'},
                },
                dashboardRoute('dashboard', '/report/dashboard'),
            ],
        },
        {
            path     : '/accept',
            name     : 'accept',
            component: AcceptLanding,
            meta     : {requires: 'accept'},
            redirect : () => getStoredTab('accept', '/accept/te-doen'),
            children : [
                {
                    path     : 'te-doen',
                    component: TabTeDoen,
                    meta     : {requires: 'accept'},
                },
                {
                    path     : 'geaccepteerd',
                    component: TabGeaccepteerd,
                    meta     : {requires: 'accept'},
                },
                {
                    path     : 'afgewezen',
                    component: TabAfgewezen,
                    meta     : {requires: 'accept'},
                },
            ],
        },
        {
            path     : '/report_manager',
            name     : 'report manager',
            component: ReporterManagerLanding,
            meta     : {requires: Permission.manage_reporters},
            redirect : () => getStoredTab('report manager', '/report_manager/user'),
            children : [
                {
                    path     : 'user',
                    component: TabGebruikers,
                    meta     : { requires: Permission.manage_reporters },
                    props    : { selectableRoles: [Role.reporter] }
                    
                }
            ],
        },
        {
            path     : '/dashboard',
            name     : 'dashboard',
            component: DashboardLanding,
            meta     : {requires: Permission.dashboard},
            children : [
                dashboardRoute('', '/dashboard'),
            ],
        },
        {
            path    : '/:pathMatch(.*)*',
            redirect: '/',
        },
    ],
});

function getStoredTab(perm: string, def: string): string {
    const stored: string | null = localStorage.getItem(`activeTab:${perm}`);
    if (stored) {
        const hasComponent: boolean = router.resolve(stored).matched.some(
            (r: any) => r.components && Object.keys(r.components).length > 0
        );
        if (hasComponent) {
            return stored;
        }
        localStorage.removeItem(`activeTab:${perm}`);
    }
    return def;
}

router.beforeEach(async function (to: any, _from: any, next: any) {
    const perm = to.meta.requires;
    if (!perm) {
        next();
    } else if (!await meHasPermission(perm)) {
        next('/');
    } else {
        if (to.path !== '/') {
            localStorage.setItem(`activeTab:${perm}`, to.path);
        }
        next();
    }
});

export default router;
